import React, { useMemo } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import { type PromoRequestWarningSummary, getPromoRequestWarningCountsBySeverity } from '~/utils/promoRequestWarnings';

type Props = {
  warningSummaries: Array<PromoRequestWarningSummary>;
};

function getSeverityChipColor(severity: string): ChipProps['color'] {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
}

function getSeverityLabel(severity: string): string {
  return `${severity.slice(0, 1).toUpperCase()}${severity.slice(1)}`;
}

export default function PromoRequestWarningsSummaryButton({ warningSummaries }: Props) {
  const severityCounts = useMemo(
    () => getPromoRequestWarningCountsBySeverity(warningSummaries.map(({ warning }) => warning)),
    [warningSummaries],
  );
  const warningsBySeverity = useMemo(() => {
    const summariesBySeverity = new Map<string, Array<PromoRequestWarningSummary>>();

    warningSummaries.forEach((summary) => {
      const severity = summary.warning.severity;
      summariesBySeverity.set(severity, (summariesBySeverity.get(severity) ?? []).concat(summary));
    });

    return summariesBySeverity;
  }, [warningSummaries]);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" color={warningSummaries.length > 0 ? 'text.primary' : 'text.secondary'}>
        Warnings
      </Typography>
      {severityCounts.length > 0 ? (
        severityCounts.map(({ severity, count }) => (
          <Box key={severity} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              variant="outlined"
              color={getSeverityChipColor(severity)}
              label={`${getSeverityLabel(severity)}: ${count}`}
            />
            <DebugModal
              type="generic"
              hideIcon
              title={`Promo Request ${getSeverityLabel(severity)}s (${count})`}
              data={{
                total: warningSummaries.length,
                totalsBySeverity: Object.fromEntries(
                  severityCounts.map(({ severity: countSeverity, count: severityCount }) => [
                    countSeverity,
                    severityCount,
                  ]),
                ) as Record<string, number>,
                warnings: warningsBySeverity.get(severity) ?? [],
              }}
              buttonProps={{
                sx: {
                  textTransform: 'none',
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                },
              }}
            >
              View
            </DebugModal>
          </Box>
        ))
      ) : (
        <Chip size="small" variant="outlined" label="0" />
      )}
    </Stack>
  );
}
